import { useState, useContext } from "react";
import { Link, useNavigate } from 'react-router-dom';
import { TenantContext } from "../../pages/tenants/tenant-detail";
import { arrayAny } from "../../utils/arrayUtils";
import { NewPeriodModal } from "./new-period-modal";
import moment from "moment-timezone";

export const humanizeReason = reason => {

    switch (reason) {
        case "new":
            return "New Tenancy";
        case "move":
            return "Moved Property";
        case "rentChange":
            return "Rent Change";
        default:
            return reason;
    }

};

const RentalPeriodActionButtons = ({ editClicked, deleteClicked, quickEndClicked }) => {

    return (
        <div className="mb-2">
            <button type="button" className="btn btn-primary" data-bs-toggle="dropdown">
                Action&nbsp;&nbsp;<span className="caret"></span>
            </button>
            <div className="dropdown-menu">
                <button onClick={editClicked} type="button" className="dropdown-item"><i className="fa fa-pencil fa-lg"></i>&nbsp;&nbsp;Edit</button>
                <button onClick={deleteClicked} type="button" className="dropdown-item"><i className="fa fa-trash fa-lg"></i>&nbsp;&nbsp;Delete</button>
                <button onClick={quickEndClicked} type="button" className="dropdown-item"><i className="fa fa-times fa-lg"></i>&nbsp;&nbsp;Quick End</button>
            </div>
        </div>
    );

};

const RentalPeriodTable = ({ rentalPeriods, setTenant, tenancy, tenant }) => {

    const [showEdit, setShowEdit] = useState(false);
    const [selectedPeriod, setSelectedPeriod] = useState(null);

    const dateDiff = (date1, date2) => {
        const diffTime = (date2 - date1);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays;
    };

    const getStateRemaining = (startDate, endDate) => {

        if (startDate) {
            const diffTime = new Date() - new Date(startDate);
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            if (diffDays < 0) {
                return "N/A";
            }
        }

        return endDate && dateDiff(new Date(), new Date(endDate)) > 0 ?
            dateDiff(new Date(), new Date(endDate)) :
            endDate && dateDiff(new Date(), new Date(endDate)) <= 0 ?
                "Ended" :
                "Rolling"
    };

    const getStateEnd = (startDate, endDate) => {

        if (startDate) {
            const diffTime = new Date() - new Date(startDate);
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            if (diffDays < 0) {
                return "N/A";
            }
        }

        return endDate ? moment.utc(new Date(endDate)).format('DD/MM/YYYY') : "Rolling"

    };

    const getDuration = (startDate, endDate) => {

        if (startDate) {
            const diffTime = new Date() - new Date(startDate);
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            if (diffDays < 0) {
                return "N/A";
            }
        }

        return dateDiff(new Date(startDate), endDate ? new Date(endDate) : new Date());
    };

    const endPeriod = (tenancy, period) => {
        const existingTenancy = tenant.tenancies.find(t => t === tenancy);

        const existingPeriod = existingTenancy.rentalPeriods.find(rp => rp === period);

        const endSeed = new Date();
        const endDate = new Date(Date.UTC(endSeed.getFullYear(), endSeed.getMonth(), endSeed.getDate(), 23, 59, 59, 999));
        existingPeriod.period.end = endDate.toISOString();
        setTenant(structuredClone(tenant));
    };

    const deletePeriod = (tenancy, period) => {
        const existingTenancy = tenant.tenancies.find(t => t === tenancy);

        const existingPeriodIndex = existingTenancy.rentalPeriods.findIndex(rp => rp === period);
        existingTenancy.rentalPeriods.splice(existingPeriodIndex, 1);
        setTenant(structuredClone(tenant));
    };

    return (

        <>
            {selectedPeriod && <NewPeriodModal
                show={showEdit}
                cancel={() => { setShowEdit(false); setSelectedPeriod(null); }}
                setTenant={setTenant}
                tenancy={tenancy}
                tenant={tenant}
                period={selectedPeriod}
            />}

            {/* Desktop / large screens: responsive table with horizontal scroll if needed */}
            <div className="d-none d-lg-block">
                <div className="table-responsive">
                    <table className="table table-bordered align-middle table-hover">
                        <thead className="table-dark">
                            <tr>
                                <th className="text-break">#</th>
                                <th className="align-middle text-center text-break">Reason</th>
                                <th className="align-middle text-center text-break">Tenancy Start</th>
                                <th className="align-middle text-center text-break">Tenancy End</th>
                                <th className="align-middle text-center text-break">Duration (days)</th>
                                <th className="align-middle text-center text-break">Remaining (days)</th>
                                <th className="align-middle text-center text-break">Rent (monthly)</th>
                                <th className="align-middle text-center text-break">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(rentalPeriods && arrayAny(rentalPeriods) && rentalPeriods.map((period, i) =>
                                <tr key={`period-${i}`}>
                                    <td className="align-middle text-center text-break">{i + 1}</td>
                                    <td className="align-middle text-center text-break">{humanizeReason(period.reason)}</td>
                                    <td className="align-middle text-center text-break">{moment.utc(new Date(period.period.start)).format('DD/MM/YYYY')}</td>
                                    <td className="align-middle text-center text-break">{getStateEnd(period.period.start, period.period.end)}</td>
                                    <td className="align-middle text-center text-break">{getDuration(period.period.start, period.period.end)}</td>
                                    <td className="align-middle text-center text-break">{getStateRemaining(period.period.start, period.period.end)}
                                    </td>
                                    <td className="align-middle text-center text-break">{period.rentalValue}</td>
                                    <td className="align-middle text-center text-break">
                                        <RentalPeriodActionButtons
                                            editClicked={() => { setSelectedPeriod(period); setShowEdit(true) }}
                                            deleteClicked={() => deletePeriod(tenancy, period)}
                                            quickEndClicked={() => endPeriod(tenancy, period)}
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Mobile / small screens: stacked cards for readability and tappable actions */}
            <div className="d-block d-lg-none">
                {rentalPeriods && arrayAny(rentalPeriods) ? rentalPeriods.map((period, i) => (
                    <div className="card mb-2" key={`period-mobile-${i}`}>
                        <div className="card-body p-2">
                            <div className="d-flex justify-content-between align-items-start">
                                <div>
                                    <div className="fw-bold">{humanizeReason(period.reason)}</div>
                                    <div className="small text-muted">Start: {moment.utc(new Date(period.period.start)).format('DD/MM/YYYY')}</div>
                                    <div className="small text-muted">End: {getStateEnd(period.period.start, period.period.end)}</div>
                                </div>
                                <div className="text-end">
                                    <div className="fw-bold">{period.rentalValue}</div>
                                    <div className="small text-muted">Remaining: {getStateRemaining(period.period.start, period.period.end)}</div>
                                    <div className="small text-muted">Duration: {getDuration(period.period.start, period.period.end)}d</div>
                                </div>
                            </div>

                            <div className="mt-2 d-flex gap-2">
                                <button type="button" className="btn btn-sm btn-primary" onClick={() => { setSelectedPeriod(period); setShowEdit(true); }}>Edit</button>
                                <button type="button" className="btn btn-sm btn-danger" onClick={() => deletePeriod(tenancy, period)}>Delete</button>
                                <button type="button" className="btn btn-sm btn-secondary" onClick={() => endPeriod(tenancy, period)}>Quick End</button>
                            </div>
                        </div>
                    </div>
                )) : <div className="text-muted">No rental periods</div>}
            </div>

        </>

    );

};

export const TenanciesTab = () => {

    const [selectedTenancy, setSelectedTenancy] = useState(null);
    const [showAddNewPeriodModal, setShowAddNewPeriodModal] = useState(false);
    const navigate = useNavigate();

    const { tenant, setTenant, validationErrors, setValidationErrors } = useContext(TenantContext);

    const addNewPeriod = tenancy => {
        setSelectedTenancy(tenancy);
        setShowAddNewPeriodModal(true);
    };

    const addInventory = (tenancyIndex, type = 'periodic') => {
        // navigate to inventory creation page with tenancy index and type
        navigate(`/tenant/${tenant.id}/inventory/new?tenancyIndex=${tenancyIndex}&type=${encodeURIComponent(type)}`);
    };

    const editInventory = (inventoryId) => {
        navigate(`/tenant/${tenant.id}/inventory/${inventoryId}`);
    };

    return (
        <div className="row">

            <NewPeriodModal
                show={showAddNewPeriodModal}
                cancel={() => setShowAddNewPeriodModal(false)}
                setTenant={setTenant}
                tenancy={selectedTenancy}
                tenant={tenant}
            />
            {/* Inventory details are now routed to a dedicated page; thumbnails/link navigate to that route */}

            <div className="tab-content panel p-3 rounded">
                <div className="tab-pane fade active show" id="default-tab-3">
                    <h3>Tenancies | {tenant.displayName}</h3>
                    <p>
                        Tenancies can start when a tenant moves in, or when they move room/property. New tenancies are created when you issue a Section 13 rent change notice.
                    </p>
                    {/* Desktop/large: table view */}
                    <div className="d-none d-lg-block table-responsive">
                        <table className="table table-bordered align-middle table-hover">
                            <thead className="table-dark">
                                <tr>
                                    <th className="text-break">#</th>
                                    <th className="align-middle text-center text-break">Property</th>
                                    <th className="align-middle text-center text-break">Room #</th>
                                    <th className="align-middle text-center text-break">Rent Due Day</th>
                                    <th className="align-middle text-center text-break">Tenancy Periods</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(tenant && arrayAny(tenant.tenancies) && tenant.tenancies.map((tenancy, i) =>
                                    <tr key={`${tenant.id}-${i}`}>
                                        <td className="align-middle text-center text-break">{i + 1}</td>
                                        <td className="align-middle text-center text-wrap text-break">{tenant.property.property.name}</td>
                                        <td className="align-middle text-center text-break">
                                            <div className="w-100">
                                                <input
                                                    type="number"
                                                    className="form-control w-100"
                                                    value={tenancy.roomNumber ?? 1}
                                                    onChange={(e) => {
                                                        const newValue = e.target.value ? parseInt(e.target.value) : 1;
                                                        const updatedTenancies = [...tenant.tenancies];
                                                        const tenancyIndex = updatedTenancies.findIndex(t => t === tenancy);
                                                        updatedTenancies[tenancyIndex] = { ...tenancy, roomNumber: newValue };
                                                        setTenant({ ...tenant, tenancies: updatedTenancies });
                                                    }}
                                                    min="1"
                                                    step="1"
                                                />
                                                <div className="d-flex align-items-center gap-2 mt-1">
                                                    <Link to={`/tenant/${tenant.id}/tenancy/${i}/inventories`} className="btn btn-sm btn-outline-primary d-flex align-items-center" aria-label={`View inventories for tenancy ${i + 1}`}>
                                                        <span>Inventories</span>
                                                        <span className="badge bg-secondary ms-2">{(tenancy.inventories || []).length}</span>
                                                    </Link>
                                                    <div className="btn-group">
                                                        <button type="button" className="btn btn-sm btn-outline-secondary dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false">+</button>
                                                        <div className="dropdown-menu">
                                                            <button type="button" className="dropdown-item" onClick={() => addInventory(i, 'move-in')}>Add Move-In</button>
                                                            <button type="button" className="dropdown-item" onClick={() => addInventory(i, 'move-out')}>Add Move-Out</button>
                                                            <button type="button" className="dropdown-item" onClick={() => addInventory(i, 'periodic')}>Add Periodic</button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="align-middle text-center">
                                            <div className="w-100">
                                                <select
                                                    className="form-select w-100"
                                                    value={tenant.rentDueDayOfMonth ?? 1}
                                                    onChange={(e) => {
                                                        const newValue = parseInt(e.target.value);
                                                        setTenant({ ...tenant, rentDueDayOfMonth: newValue });
                                                    }}
                                                >
                                                    {[...Array(28)].map((_, i) => (
                                                        <option key={i + 1} value={i + 1}>{i + 1}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        </td>
                                        <td className="align-middle text-center">
                                            <div className="text-start mb-2">
                                                <div className="btn-group">
                                                    <button type="button" className="btn btn-primary dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false">
                                                        Action
                                                    </button>
                                                    <div className="dropdown-menu">
                                                        <button type="button" onClick={() => addNewPeriod(tenancy)} className="dropdown-item"><i className="fa fa-plus fa-lg"></i>&nbsp;&nbsp;Add Period</button>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Inventories link moved under Room # field; thumbnails removed to simplify layout */}

                                            <RentalPeriodTable
                                                rentalPeriods={tenancy.rentalPeriods}
                                                setTenant={setTenant}
                                                tenancy={tenancy}
                                                tenant={tenant}
                                            />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Mobile/portrait: stacked cards to avoid horizontal overflow */}
                    <div className="d-block d-lg-none">
                        {tenant && arrayAny(tenant.tenancies) ? tenant.tenancies.map((tenancy, i) => (
                            <div className="card mb-3" key={`tenancy-mobile-${i}`}>
                                <div className="card-body">
                                    <div className="d-flex justify-content-between align-items-start">
                                        <div>
                                            <div className="fw-bold">{tenant.property.property.name}</div>
                                            <div className="small text-muted">Tenancy #{i + 1}</div>
                                        </div>
                                        <div>
                                            <div className="btn-group">
                                                <button type="button" className="btn btn-sm btn-primary dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false">
                                                    Action
                                                </button>
                                                <div className="dropdown-menu dropdown-menu-end">
                                                    <button type="button" onClick={() => addNewPeriod(tenancy)} className="dropdown-item"><i className="fa fa-plus fa-lg"></i>&nbsp;&nbsp;Add Period</button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="mt-2">
                                        <label className="form-label small mb-1">Room #</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={tenancy.roomNumber ?? 1}
                                            onChange={(e) => {
                                                const newValue = e.target.value ? parseInt(e.target.value) : 1;
                                                const updatedTenancies = [...tenant.tenancies];
                                                const tenancyIndex = updatedTenancies.findIndex(t => t === tenancy);
                                                updatedTenancies[tenancyIndex] = { ...tenancy, roomNumber: newValue };
                                                setTenant({ ...tenant, tenancies: updatedTenancies });
                                            }}
                                            min="1"
                                            step="1"
                                        />
                                        <div className="d-flex align-items-center gap-2 mt-2">
                                            <Link to={`/tenant/${tenant.id}/tenancy/${i}/inventories`} className="btn btn-sm btn-outline-primary d-flex align-items-center" aria-label={`View inventories for tenancy ${i + 1}`}>
                                                <span>Inventories</span>
                                                <span className="badge bg-secondary ms-2">{(tenancy.inventories || []).length}</span>
                                            </Link>
                                                    <div className="btn-group">
                                                        <button type="button" className="btn btn-sm btn-outline-secondary dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false">+</button>
                                                        <div className="dropdown-menu">
                                                            <button type="button" className="dropdown-item" onClick={() => addInventory(i, 'move-in')}>Add Move-In</button>
                                                            <button type="button" className="dropdown-item" onClick={() => addInventory(i, 'move-out')}>Add Move-Out</button>
                                                            <button type="button" className="dropdown-item" onClick={() => addInventory(i, 'periodic')}>Add Periodic</button>
                                                        </div>
                                                    </div>
                                        </div>
                                    </div>

                                    <div className="mt-2">
                                        <label className="form-label small mb-1">Rent Due Day</label>
                                        <select
                                            className="form-select"
                                            value={tenant.rentDueDayOfMonth ?? 1}
                                            onChange={(e) => {
                                                const newValue = parseInt(e.target.value);
                                                setTenant({ ...tenant, rentDueDayOfMonth: newValue });
                                            }}
                                        >
                                            {[...Array(28)].map((_, j) => (
                                                <option key={j + 1} value={j + 1}>{j + 1}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="mt-3">
                                        <RentalPeriodTable
                                            rentalPeriods={tenancy.rentalPeriods}
                                            setTenant={setTenant}
                                            tenancy={tenancy}
                                            tenant={tenant}
                                        />
                                    </div>
                                </div>
                            </div>
                        )) : <div className="text-muted">No tenancies</div>}
                    </div>
                    {/* (Outlet moved to TenantDetail so nested routes render inside the parent route element) */}
                </div>
            </div>
        </div>
    );
};